/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, ICoreContext, IImageSettings, Image } from '@msdyn365-commerce/core';
import { SalesLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl, getProductPageUrlSync, ObjectExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ReturnOrderProduct } from '../../order-return-initiate-product.component';

import React from 'react';

export interface IOrderReturnInitiateTableRowData {
    salesLine: SalesLine;
    returnProduct: ReturnOrderProduct;
}

export interface IOrderReturnInitiateTableRowResources {
    productSKULabel: string;
    returnStatusLabel: string;
    kitContentsToggleLabel: string;
}

export interface IOrderReturnInitiateTableRowProps extends IComponentProps<IOrderReturnInitiateTableRowData> {
    context: ICoreContext;
    className: string;

    imageSettings?: IImageSettings;
    resources: IOrderReturnInitiateTableRowResources;
    onChange(): void;
}

const defaultImageSettings: IImageSettings = {
    viewports: {
        xs: { q: 'w=50&h=50&m=6', w: 0, h: 0 },
        lg: { q: 'w=80&h=80&m=6', w: 0, h: 0 }
    },
    lazyload: true,
    cropFocalRegion: true
};

const _renderProductImage = (props: IOrderReturnInitiateTableRowProps, simpleProduct: SimpleProduct): JSX.Element => {
    const fallbackImageUrl = getFallbackImageUrl(simpleProduct.ItemId, props.context.actionContext.requestContext.apiSettings);
    const containerClassName = `${props.className}__product-image-container`;

    return (
        <div className={containerClassName}>
            <Image
                requestContext={props.context.actionContext.requestContext}
                className={`${containerClassName}__product-image`}
                altText={simpleProduct.Name}
                src={simpleProduct.PrimaryImageUrl ?? ''}
                fallBackSrc={fallbackImageUrl}
                gridSettings={props.context.request.gridSettings!}
                imageSettings={props.imageSettings ?? defaultImageSettings}
                loadFailureBehavior='empty'
            />
        </div>
    );
};

const _getProductUrl = (props: IOrderReturnInitiateTableRowProps, simpleProduct: SimpleProduct): string => {
    if (!StringExtensions.isNullOrWhitespace(simpleProduct.Name) && !ObjectExtensions.isNullOrUndefined(simpleProduct.RecordId)) {
        return getProductPageUrlSync(simpleProduct.Name!, simpleProduct.RecordId, props.context.actionContext);
    }
    return '';
};

const _generateQuantityOptions = (className: string, maxQuantity: number) => {
    const nodes = [];
    for (let i = 0; i <= maxQuantity; i++) {
        nodes.push(
            <option key={i} className={`${className}__quantity__select-menu__number`} value={i}>
                {i}
            </option>
        );
    }
    return nodes;
};

const _generateQuantitySelectMenu = (props: IOrderReturnInitiateTableRowProps): JSX.Element => {
    const { returnProduct } = props.data;
    return (
        <select
            className={`${props.className}__quantity__select-menu`}
            value={returnProduct.itemReturnQuantity}
            disabled={!returnProduct.isEligible}
            onChange={(e) => {
                returnProduct.itemReturnQuantity = +e.target.value;
                returnProduct.isSelected = returnProduct.itemReturnQuantity > 0;
                props.onChange();
            }}
        >
            {_generateQuantityOptions(props.className, returnProduct.eligibleReturnQty)}
        </select>
    );
};

// Toggles selection for an eligible line. Checking defaults the quantity to the line's eligible
// return quantity; unchecking resets it to 0 (BRD reqs 5a-5d).
const _onToggleSelected = (props: IOrderReturnInitiateTableRowProps): void => {
    const { returnProduct } = props.data;
    returnProduct.isSelected = !returnProduct.isSelected;
    returnProduct.itemReturnQuantity = returnProduct.isSelected ? returnProduct.eligibleReturnQty : 0;
    props.onChange();
};

const OrderReturnInitiateTableCell: React.FC<IOrderReturnInitiateTableRowProps & { isExpanded?: boolean; onToggleKitContents?: () => void }> = (props): JSX.Element => {
    const { returnProduct } = props.data;
    const productUrl = _getProductUrl(props, returnProduct.simpleProduct);
    const hasKitContents = returnProduct.isKitParent && returnProduct.kitChildren.length > 0;

    return (
        <>
            <td>
                <input
                    className={`${props.className}__checkbox-input`}
                    type="checkbox"
                    disabled={!returnProduct.isEligible}
                    onChange={() => _onToggleSelected(props)}
                    checked={returnProduct.isSelected}
                />
            </td>
            <td>
                {_renderProductImage(props, returnProduct.simpleProduct)}
            </td>
            <td>
                <div className={`${props.className}__return-product-info`}>
                    <a className={`${props.className}__return-product-name`} href={productUrl}>
                        {returnProduct.simpleProduct.Name}
                    </a>
                    <div className={`${props.className}__return-product-sku`}>
                        {`${props.resources.productSKULabel}${returnProduct.simpleProduct.ItemId}`}
                    </div>
                    {hasKitContents &&
                        <a
                            className={`${props.className}__kit-contents-button`}
                            role='button'
                            aria-expanded={!!props.isExpanded}
                            onClick={props.onToggleKitContents}
                        >
                            {props.resources.kitContentsToggleLabel}
                        </a>
                    }
                </div>
            </td>
            <td className={`${props.className}__return-product-quantity`}>
                {_generateQuantitySelectMenu(props)}
            </td>
        </>
    );
};

// Read-only display of a kit component line shown beneath its parent kit row. Kit components cannot
// be returned individually; only the parent kit is returnable (BRD req 7).
const OrderReturnInitiateKitChildCell: React.FC<IOrderReturnInitiateTableRowProps & { simpleProduct: SimpleProduct }> = (props) => {
    const productUrl = _getProductUrl(props, props.simpleProduct);

    return (
        <>
            <td />
            <td>
                {_renderProductImage(props, props.simpleProduct)}
            </td>
            <td>
                <div className={`${props.className}__return-product-info`}>
                    <a className={`${props.className}__return-product-name`} href={productUrl}>
                        {props.simpleProduct.Name}
                    </a>
                    <div className={`${props.className}__return-product-sku`}>
                        {`${props.resources.productSKULabel}${props.simpleProduct.ItemId}`}
                    </div>
                </div>
            </td>
            <td />
        </>
    );
};

/**
 * Table row component. Renders the returnable line and, for kit parents, a collapsible section of
 * read-only kit component lines beneath it (mirrors the cart page kit contents dropdown, starts
 * collapsed).
 * @param props - Configuration of the component.
 * @returns React node for the row.
 */
export const OrderReturnInitiateTableRowComponent: React.FC<IOrderReturnInitiateTableRowProps> = (props: IOrderReturnInitiateTableRowProps) => {
    const { returnProduct } = props.data;
    const rowClassName = `${props.className}__row`;
    const childRowClassName = `${props.className}__kit-child-row`;

    const [isKitContentsExpanded, setIsKitContentsExpanded] = React.useState(false);

    return (
        <tbody className={props.className}>
            <tr className={`${rowClassName}${returnProduct.isEligible ? '' : ` ${rowClassName}--disabled`}`}>
                <OrderReturnInitiateTableCell
                    {...props}
                    isExpanded={isKitContentsExpanded}
                    onToggleKitContents={() => setIsKitContentsExpanded(!isKitContentsExpanded)}
                />
            </tr>
            {returnProduct.isKitParent && isKitContentsExpanded && returnProduct.kitChildren.map(child => (
                <tr className={childRowClassName} key={child.simpleProduct.ItemId}>
                    <OrderReturnInitiateKitChildCell {...props} simpleProduct={child.simpleProduct} />
                </tr>
            ))}
        </tbody>
    );
};
